import { OverlayContainer } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Input, Output, Signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MATERIAL_ANIMATIONS } from '@angular/material/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { FlyoutModel } from '@libs/common/models';
import { IconComponent } from '@libs/components/icons';
import { ViewportService } from '@libs/shared/services';

@Component({
  selector: 'ui-flyout',
  templateUrl: './flyout.component.html',
  imports: [
    MatCardModule,
    CommonModule,
    MatMenuModule,
    MatButtonModule,
    RouterLink,
    IconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: MATERIAL_ANIMATIONS, useValue: { animationsDisabled: true } },
  ],
})
export class FlyoutComponent implements AfterViewInit {
  private readonly overlayContainer: OverlayContainer = inject(OverlayContainer);
  private readonly viewportService: ViewportService = inject(ViewportService);

  @Input() public data!: FlyoutModel;
  @Input() public useRouterLinks: boolean = false;
  @Output() public menuOpened: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(MatMenuTrigger) public menuTrigger!: MatMenuTrigger;

  public isMobile: Signal<boolean> = toSignal(this.viewportService.isMobile(), { initialValue: false });
  public buttonHovered: boolean = false;
  public menuHovered: boolean = false;

  public ngAfterViewInit(): void {
    this.menuTrigger.menuOpened.subscribe(() => {
      this.menuOpened.emit();
      setTimeout(() => {
        const firstMenuItem: Element | null | undefined = this.getOwnPanel()?.querySelector('.mat-mdc-menu-item');

        firstMenuItem?.classList.remove('cdk-program-focused');
      }, 0);
    });
  }

  public buttonTouch(): void {
    this.menuTrigger.toggleMenu();
  }

  public buttonEnter(): void {
    if (this.isMobile()) {
      return;
    }

    this.buttonHovered = true;
    this.menuTrigger.openMenu();
    this.addMenuPanelListeners();
  }

  public buttonLeave(): void {
    this.buttonHovered = false;
    this.closeIfNothingHovered();
  }

  public menuOver(): void {
    this.menuHovered = true;
  }

  public menuLeave(): void {
    this.menuHovered = false;
    this.closeIfNothingHovered();
  }

  private closeIfNothingHovered(): void {
    setTimeout(() => {
      if (!this.menuHovered && !this.buttonHovered) {
        this.menuTrigger.closeMenu();
      }
    }, 175);
  }

  private getOwnPanel(): HTMLElement | null {
    const panelId: string | undefined = this.menuTrigger.menu?.panelId;

    if (!panelId) {
      return null;
    }

    return this.overlayContainer.getContainerElement().querySelector<HTMLElement>(`#${panelId}`);
  }

  private getMenuPanelContainer(): HTMLElement | null | undefined {
    return this.getOwnPanel()?.parentElement;
  }

  private addMenuPanelListeners(): void {
    const panel: HTMLElement | null | undefined = this.getMenuPanelContainer();

    if (panel) {
      panel.onmouseover = this.menuOver.bind(this);
      panel.onmouseleave = this.menuLeave.bind(this);
    }
  }
}
